import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './ProductTable.css';
import InlineEditBadge from './InlineEditBadge';
import SiteFilterPills from './SiteFilterPills';
import InlineEditCell from './InlineEditCell';
import PaginationCompact from './PaginationCompact';
import formatContenido from '../utils/formatContenido';
import { API_BASE } from '../config';

// Whitelist canónica de UDM. Acepta tanto KG/G/ML/L (mayúscula, formato extractor) como
// las versiones minúsculas que el LLM TIA Ecuador devuelve (ml, g, kg, l, un, cc...).
const UDM_WHITELIST = ['ml', 'l', 'g', 'kg', 'mg', 'cc', 'oz', 'un', 'pack'];

// Round 2 (2026-06-07) — Item 13. Subcomponente collapsible para histórico
// de precios on-demand. Fetch lazy + cache local en state del propio item.
// Hook + componentes separados para que el botón Histórico y la tabla puedan
// renderizarse en lugares distintos (el botón en línea con Eliminar, la tabla
// en un panel full-width debajo de los botones).
function usePriceHistory(idCatalogo, sitio) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const fetchHistory = useCallback(async () => {
    if (data !== null) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (sitio) params.set('sitio', sitio);
      const url = `${API_BASE}/api/match/${encodeURIComponent(idCatalogo)}/price-history${params.toString() ? `?${params.toString()}` : ''}`;
      // Pedimos siempre incluir tomas faltantes para mostrar "No disponible"
      params.set('includeAllTomas', '1');
      const url2 = `${API_BASE}/api/match/${encodeURIComponent(idCatalogo)}/price-history?${params.toString()}`;
      const res = await fetch(url2);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const arr = Array.isArray(json.history) ? json.history : (Array.isArray(json.data) ? json.data : []);
      setData(arr);
    } catch (e) {
      console.error('PriceHistory fetch error:', e);
      setError(e.message || 'Error al cargar histórico');
    } finally { setLoading(false); }
  }, [idCatalogo, sitio, data]);
  const toggle = useCallback((e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && data === null) fetchHistory();
  }, [open, data, fetchHistory]);
  return { open, loading, data, error, toggle };
}

function PriceHistoryButton({ open, onToggle }) {
  return (
    <button
      type="button"
      className={`price-history-toggle ${open ? 'is-open' : ''}`}
      onClick={onToggle}
      aria-expanded={open}
      title="Ver histórico de precios"
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
      <span>Histórico</span>
      <span className="caret">▾</span>
    </button>
  );
}

function PriceHistoryPanel({ loading, data, error }) {
  const fmtPrice = (v) => {
    if (v === null || v === undefined || v === '') return '-';
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return String(v);
    return `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const fmtDate = (v) => {
    if (!v) return '-';
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yy = d.getFullYear();
      return `${dd}/${mm}/${yy}`;
    } catch { return String(v); }
  };
  return (
    <div className="price-history-panel" onClick={(e) => e.stopPropagation()}>
      {loading && (<div className="price-history-loading"><span className="mini-spinner" /><span>Cargando histórico…</span></div>)}
      {!loading && error && (<div className="price-history-error">{error}</div>)}
      {!loading && !error && data && data.length === 0 && (<div className="price-history-empty">Sin histórico disponible</div>)}
      {!loading && !error && data && data.length > 0 && (
        <table className="price-history-table">
          <thead><tr><th>Toma</th><th>Fecha</th><th>Precio</th><th>Original</th><th>Dto</th></tr></thead>
          <tbody>
            {data.map((r, i) => {
              if (r.unavailable) {
                return (
                  <tr key={i} className="ph-unavailable">
                    <td>{r.intake ?? '-'}</td>
                    <td>{fmtDate(r.fecha)}</td>
                    <td colSpan={3} className="ph-unav-cell">No disponible</td>
                  </tr>
                );
              }
              const sale = r.sale_price != null ? parseFloat(r.sale_price) : null;
              const orig = r.original_price != null ? parseFloat(r.original_price) : null;
              let dto = null;
              if (sale && orig && orig > sale) dto = Math.round(((orig - sale) / orig) * 100);
              return (
                <tr key={i}>
                  <td>{r.intake ?? '-'}</td>
                  <td>{fmtDate(r.fecha)}</td>
                  <td className="ph-price">{fmtPrice(sale)}</td>
                  <td>{fmtPrice(orig)}</td>
                  <td className="ph-discount">{dto != null ? `-${dto}%` : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Componente compuesto (botón+panel inline) — para casos donde el caller no quiere separarlos.
function PriceHistoryToggle({ idCatalogo, sitio }) {
  const ph = usePriceHistory(idCatalogo, sitio);
  return (
    <div className="price-history-section">
      <PriceHistoryButton open={ph.open} onToggle={ph.toggle} />
      {ph.open && <PriceHistoryPanel loading={ph.loading} data={ph.data} error={ph.error} />}
    </div>
  );
}

function MatchCardActions({ idCatalogo, sitio, onDelete }) {
  const ph = usePriceHistory(idCatalogo, sitio);
  return (
    <>
      <div className="match-actions-row">
        {idCatalogo && <PriceHistoryButton open={ph.open} onToggle={ph.toggle} />}
        {onDelete && (
          <button
            className="delete-match-btn inline"
            onClick={(e) => { e.stopPropagation(); onDelete(idCatalogo); }}
            title="Eliminar este match"
            aria-label="Eliminar match"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
            </svg>
            Eliminar
          </button>
        )}
      </div>
      {ph.open && (
        <div className="match-history-fullwidth">
          <PriceHistoryPanel loading={ph.loading} data={ph.data} error={ph.error} />
        </div>
      )}
    </>
  );
}

// Round 2 (2026-06-07) — FieldEditor reemplazado por InlineEditCell (item 10).
// El componente vive ahora en ./InlineEditCell.{js,css}, replica el patrón
// del SKU editable de PT v2 (lápiz on-hover + save/cancel inline + Enter/Esc).

function ProductTable({
  type,
  searchTerm,
  onSearchChange,
  tomaTable,
  onAISearch,
  onSelectProduct,
  selectedProduct,
  onAIMatch,
  matchedIndexes = [],
  similarityScores = [],
  isMatching = false,
  matchingProduct = null,
  matchingLogs = [],
  onCancelMatching = null,
  smartQueryDebug = null,
  matchingDebug = null,
  productsToExpand = new Set(),
  showOnlyMatched = false,
  excludeMatched = false,
  onDropCatalogProduct = null,
  isDragOver = false,
  onDragOver = null,
  onDragLeave = null,
  onDrop = null,
  onDeleteMatch = null,
  isAddingMatch = false,
  overrideProducts = null,
  // Nuevos props UDM/CONTENIDO
  onCatalogPatch = null,            // (idCatalogo, { udm?, contenido? }) => Promise
  maestroBadges = null,             // { udm, contenido, manually_edited_fields, link_id } — del maestro seleccionado, para columna Matcheos
  // Site filter pills (columna Catálogo)
  siteFilter = null,                // Set<string> | null = sin filtro
  onToggleSiteFilter = null,        // (site) => void
  availableSites = null,            // string[] sitios detectados para los pills
  // Bulk delete (columna Matcheos)
  selectedMatchIds = null,          // Set<string|number> | null
  onToggleMatchSelection = null,    // (id_catalogo) => void
  onBulkDeleteMatches = null,       // () => void
  onClearMatchSelection = null,     // () => void
  // Refresh-tick: cuando cambia, ProductTable hace re-fetch.
  refreshTick = 0,
  // D3 (2026-06-07): nuevos props.
  matchCounts = null,             // { [id_maestro]: { count, sitios: [] } } — columna Maestro
  matchCountFilter = null,        // null/'' = todos; '0'/'1'/'2'... = N winners exactos
  onMatchCountFilterChange = null,// (newVal) => void
  matchDistribution = null,       // { '0': N, '1': M, ... } para el dropdown
  maestroFull = null,             // producto maestro completo — ficha grande en Matcheos
  maestroMatchCount = 0,          // count de matches del maestro seleccionado
  onAddMatchClick = null,         // (product) => void — botón "+ Agregar al Match" en Toma
  // UI Polish R1 (2026-06-07): empty-state contextual para columna Toma.
  // Si true y filteredProducts.length === 0 + sin search activo → mostrar mensaje
  // "Seleccioná un producto del maestro" en lugar de "No se encontraron productos".
  noMaestroSelected = false,
}) {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Para AI matching (sin paginar)
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [imageModal, setImageModal] = useState({ open: false, url: null, title: null });
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const aiMatchedCatalogIds = useMemo(() => {
    if ((type !== 'toma' && type !== 'matcheos') || allProducts.length === 0) return new Set();

    const ids = [];

    // Prioriza matches explícitos devueltos por AI (matchedIndexes)
    if (matchedIndexes.length > 0) {
      matchedIndexes.forEach(idx => {
        const product = allProducts[idx];
        if (product) {
          if (product.id_catalogo) ids.push(String(product.id_catalogo));
          if (product.title) ids.push(`title:${product.title}`);
        }
      });
    }

    // Si no hay matchedIndexes, intenta inferir matches perfectos por score >= 95
    if (ids.length === 0 && similarityScores.length > 0) {
      similarityScores
        .filter(item => item && Number(item.score) >= 95)
        .forEach(item => {
          const product = allProducts[item.index];
          if (product) {
            if (product.id_catalogo) ids.push(String(product.id_catalogo));
            if (product.title) ids.push(`title:${product.title}`);
          }
        });
    }

    return new Set(ids);
  }, [allProducts, matchedIndexes, similarityScores, type]);

  const siteWinnerIds = useMemo(() => {
    if ((type !== 'toma' && type !== 'matcheos') || allProducts.length === 0) return new Set();
    const ids = allProducts
      .filter(p => Number(p.site_winner) === 1)
      .flatMap(p => [
        p.id_catalogo ? String(p.id_catalogo) : null,
        p.title ? `title:${p.title}` : null
      ])
      .filter(Boolean);
    return new Set(ids);
  }, [allProducts, type]);

  const winnerCatalogIds = useMemo(() => {
    if (!matchingProduct || !matchingProduct.matching_process) return new Set();

    try {
      const parsed = typeof matchingProduct.matching_process === 'string'
        ? JSON.parse(matchingProduct.matching_process)
        : matchingProduct.matching_process;

      const processArray = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.matches)
          ? parsed.matches
          : null;

      if (!processArray) return new Set();

      const winners = processArray
        .filter(item => item && Number(item.site_winner) === 1 && item.id_catalogo)
        .map(item => String(item.id_catalogo));

      return new Set(winners);
    } catch (error) {
      console.error('Error al parsear matching_process:', error);
      return new Set();
    }
  }, [matchingProduct]);

  // Inicializar localSearchTerm cuando searchTerm cambia desde afuera (ej: Smart Query AI)
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
    setActiveSearchTerm(searchTerm);
    setPage(1); // Reset to page 1 when search changes externally
  }, [searchTerm]);

  // Ajustes 2604 — ítem 4: cuando el padre pasa overrideProducts (matches enriquecidos
  // desde /api/maestro/:id/catalog), renderizamos esa lista directamente sin buscar en la
  // toma. Esto permite ver matches huérfanos cuyos id_catalogo ya no existen en la toma actual.
  useEffect(() => {
    if (overrideProducts) {
      setProducts(overrideProducts);
      setAllProducts(overrideProducts);
      setTotal(overrideProducts.length);
      setTotalPages(1);
      setPage(1);
      setLoading(false);
      return;
    }
    fetchProducts();
    // eslint-disable-next-line
  }, [overrideProducts, activeSearchTerm, page, type, tomaTable, refreshTick, matchCountFilter]);

  // Obtener TODOS los productos cuando cambie el término de búsqueda (para AI matching)
  useEffect(() => {
    if (overrideProducts) return; // ya los tenemos del override
    if ((type === 'toma' || type === 'matcheos') && tomaTable) {
      fetchAllProducts();
    }
    // eslint-disable-next-line
  }, [overrideProducts, activeSearchTerm, type, tomaTable]);

  // Llamar al AI matching cuando cambien TODOS los productos del catálogo
  // Solo para la columna de matcheos, no para catálogo
  // IMPORTANTE: Solo ejecutar si hay término de búsqueda activo (searchTerm no vacío)
  useEffect(() => {
    if (type === 'matcheos' && onAIMatch && matchingProduct && activeSearchTerm) {
      onAIMatch(allProducts); // Enviar TODOS los productos (no solo la página actual)
    }
    // eslint-disable-next-line
  }, [allProducts, type, activeSearchTerm]);

  // Cerrar el panel de debug cuando cambie el producto de matching
  useEffect(() => {
    if (matchingProduct) {
      setShowDebugPanel(false);
    }
  }, [matchingProduct]);

  // Debug: verificar qué llega en smartQueryDebug
  useEffect(() => {
    if (smartQueryDebug) {
      console.log('🔍 Smart Query Debug recibido:', smartQueryDebug);
    }
  }, [smartQueryDebug]);

  // Auto-expandir productos en maestro que están en productsToExpand
  useEffect(() => {
    if (type === 'maestro' && productsToExpand.size > 0 && products.length > 0) {
      const newExpandedRows = new Set();

      getSortedProducts().forEach(({ product }, displayIndex) => {
        if (productsToExpand.has(product.cod_producto)) {
          newExpandedRows.add(displayIndex);
        }
      });

      if (newExpandedRows.size > 0) {
        setExpandedRows(newExpandedRows);
        console.log(`📦 Auto-expandiendo ${newExpandedRows.size} productos de maestro`);
      }
    }
  }, [productsToExpand, products, type]);

  const fetchProducts = async () => {
    if ((type === 'toma' || type === 'matcheos') && !tomaTable) return;

    // Si es catálogo/matcheos y no hay término de búsqueda, limpiar productos
    if ((type === 'toma' || type === 'matcheos') && !activeSearchTerm) {
      setProducts([]);
      setTotalPages(1);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const matchCountQS = (type === 'maestro' && matchCountFilter !== null && matchCountFilter !== '')
        ? `&matchCount=${encodeURIComponent(matchCountFilter)}` : '';
      const endpoint = type === 'maestro'
        ? `${API_BASE}/api/maestro/search?q=${encodeURIComponent(activeSearchTerm)}&page=${page}${matchCountQS}`
        : `${API_BASE}/api/toma/search?table=${tomaTable}&q=${encodeURIComponent(activeSearchTerm)}&page=${page}`;

      const response = await fetch(endpoint);
      const data = await response.json();

      setProducts(data.data || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error al buscar productos:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch TODOS los productos (sin paginar) para AI matching
  const fetchAllProducts = async () => {
    if ((type === 'toma' || type === 'matcheos') && !tomaTable) return;

    // Si no hay término de búsqueda, limpiar productos
    if (!activeSearchTerm) {
      setAllProducts([]);
      return;
    }

    try {
      const endpoint = `${API_BASE}/api/toma/search-all?table=${tomaTable}&q=${encodeURIComponent(activeSearchTerm)}`;
      const response = await fetch(endpoint);
      const data = await response.json();

      setAllProducts(data.data || []);
    } catch (error) {
      console.error('Error al buscar todos los productos:', error);
      setAllProducts([]);
    }
  };

  const handleSearch = (e) => {
    setLocalSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setActiveSearchTerm(localSearchTerm);
    onSearchChange(localSearchTerm);
    setPage(1); // Reset a página 1 cuando se busca
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const calculateDiscount = (originalPrice, discountedPrice) => {
    if (!originalPrice || !discountedPrice) return null;
    const original = parseFloat(originalPrice);
    const discounted = parseFloat(discountedPrice);
    if (original <= 0 || discounted >= original) return null;
    const percentage = Math.round(((original - discounted) / original) * 100);
    return percentage;
  };

  const parseTag = (tagsJson, key) => {
    try {
      if (!tagsJson) return '-';
      const tags = typeof tagsJson === 'string' ? JSON.parse(tagsJson) : tagsJson;

      // tags es un array de objetos con estructura: [{"id": 3, "tag": "CATEGORY: Hogar"}]
      if (Array.isArray(tags)) {
        const prefix = key.toUpperCase() + ':';
        const tagObj = tags.find(t => t.tag && t.tag.toUpperCase().startsWith(prefix));
        if (tagObj) {
          // Extraer el valor después del prefijo "CATEGORY: " o "SUBCATEGORY: "
          return tagObj.tag.split(':')[1]?.trim() || '-';
        }
      }

      return '-';
    } catch (error) {
      console.error('Error parsing tags:', error);
      return '-';
    }
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    setActiveSearchTerm('');
    onSearchChange('');
    setPage(1);
  };

  const handleImageClick = (imageUrl, title) => {
    setImageModal({ open: true, url: imageUrl, title });
  };

  const closeImageModal = () => {
    setImageModal({ open: false, url: null, title: null });
  };

  const toggleRowExpansion = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const combinedMatchIds = useMemo(() => {
    const union = new Set([...winnerCatalogIds]);
    siteWinnerIds.forEach(id => union.add(id));
    aiMatchedCatalogIds.forEach(id => union.add(id));
    return union;
  }, [aiMatchedCatalogIds, siteWinnerIds, winnerCatalogIds]);

  // Filtrar productos según showOnlyMatched y excludeMatched
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Para la columna de Matcheos: solo mostrar productos matcheados
    if (showOnlyMatched && (type === 'matcheos')) {
      filtered = filtered.filter(product => {
        const productIds = [
          product.id_catalogo ? String(product.id_catalogo) : null,
          product.title ? `title:${product.title}` : null
        ].filter(Boolean);

        const hasSiteWinner = product.site_winner === 1 || product.site_winner === '1';
        const isInCombinedMatches = productIds.some(id => combinedMatchIds.has(id));

        return hasSiteWinner || isInCombinedMatches;
      });
    }

    // Para la columna de Catálogo: excluir TODOS los productos que se muestran en Matcheos
    if (excludeMatched && type === 'toma') {
      filtered = filtered.filter(product => {
        const productIds = [
          product.id_catalogo ? String(product.id_catalogo) : null,
          product.title ? `title:${product.title}` : null
        ].filter(Boolean);

        const hasSiteWinner = product.site_winner === 1 || product.site_winner === '1';
        const isInCombinedMatches = productIds.some(id => combinedMatchIds.has(id));

        // Excluir si el producto está en Matcheos (misma lógica que línea 320)
        const isInMatcheos = hasSiteWinner || isInCombinedMatches;
        return !isInMatcheos;
      });
    }

    // Aplicar filtro por sitio (pills) en columna Catálogo
    if (type === 'toma' && siteFilter instanceof Set && siteFilter.size > 0) {
      filtered = filtered.filter(p => p.sitio && siteFilter.has(p.sitio));
    }

    return filtered;
  }, [products, showOnlyMatched, excludeMatched, type, combinedMatchIds, siteFilter]);

  // Ordenar productos por similarity score (si es toma o matcheos)
  const getSortedProducts = () => {
    if ((type !== 'toma' && type !== 'matcheos') || similarityScores.length === 0 || allProducts.length === 0) {
      return filteredProducts.map((p, i) => ({ product: p, originalIndex: i, score: null, reason: null }));
    }

    // Crear mapa de scores y reasons por id_catalogo (no por índice de página)
    // Los scores vienen con índices basados en allProducts, necesitamos mapearlos por id
    const scoreMap = {};
    const reasonMap = {};
    similarityScores.forEach(item => {
      const productInAll = allProducts[item.index];
      if (productInAll) {
        const productId = productInAll.id_catalogo || productInAll.title; // Usar id_catalogo o title como key
        scoreMap[productId] = item.score;
        reasonMap[productId] = item.reason;
      }
    });

    // Mapear productos filtrados con sus scores
    const productsWithScores = filteredProducts.map((p, i) => {
      const productId = p.id_catalogo || p.title;
      return {
        product: p,
        originalIndex: i,
        score: scoreMap[productId] || 0,
        reason: reasonMap[productId] || null
      };
    });

    // Ordenar por score descendente
    return productsWithScores.sort((a, b) => b.score - a.score);
  };

  // Handlers para drag and drop en matcheos
  const handleDragOver = (e) => {
    if (type === 'matcheos' && onDragOver) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      onDragOver(e);
    }
  };

  const handleDragLeave = (e) => {
    if (type === 'matcheos' && onDragLeave) {
      onDragLeave(e);
    }
  };

  const handleDrop = (e) => {
    if (type === 'matcheos' && onDrop) {
      e.preventDefault();
      onDrop(e);
    }
  };

  return (
    <div
      className={`product-table-container ${type === 'maestro' ? 'is-maestro' : ''} ${type === 'matcheos' && isDragOver ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Pills de filtro por sitio en columna Catálogo */}
      {type === 'toma' && availableSites && availableSites.length > 0 && siteFilter && onToggleSiteFilter && (
        <SiteFilterPills
          sites={availableSites}
          active={siteFilter}
          onToggle={onToggleSiteFilter}
        />
      )}

      {/* Solo mostrar buscador si NO es columna de Matcheos */}
      {type !== 'matcheos' && (
        <div className="search-box">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Buscar productos..."
                value={localSearchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              {localSearchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="clear-search-btn"
                  title="Limpiar búsqueda"
                >
                  ✕
                </button>
              )}
              <button
                type="submit"
                className="search-submit-btn"
                title="Buscar"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </form>
          <span className="results-count">
            {total} resultado{total !== 1 ? 's' : ''}
          </span>
          {/* 2026-06-08: Dropdown filtro por cantidad de matches. Solo en columna Maestro. */}
          {type === 'maestro' && onMatchCountFilterChange && matchDistribution && (
            <div className="match-filter-row">
              <label htmlFor="maestro-match-filter" className="match-filter-label">
                Cantidad de matches:
              </label>
              <select
                id="maestro-match-filter"
                className="match-filter-select"
                value={matchCountFilter ?? ''}
                onChange={(e) => {
                  onMatchCountFilterChange(e.target.value === '' ? null : e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Todos ({Object.values(matchDistribution).reduce((a, b) => a + b, 0).toLocaleString('es-AR')})</option>
                {Object.keys(matchDistribution)
                  .map(k => parseInt(k, 10))
                  .filter(n => Number.isFinite(n))
                  .sort((a, b) => a - b)
                  .map(n => (
                    <option key={n} value={String(n)}>
                      {n} {n === 1 ? 'Match' : 'Matches'} ({matchDistribution[n].toLocaleString('es-AR')})
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Round 2 (2026-06-07) — Item 6: el "N matches" desaparece (ya está en
          pastilla maestro + master card). Sólo mantenemos: spinner "Agregando..."
          y la barra de selección bulk-delete cuando hay items seleccionados. */}
      {type === 'matcheos' && (isAddingMatch || (selectedMatchIds && selectedMatchIds.size > 0 && onBulkDeleteMatches)) && (
        <div className="search-box">
          {isAddingMatch && (
            <span className="adding-match-indicator">
              <span className="mini-spinner"></span>
              Agregando match...
            </span>
          )}
          {selectedMatchIds && selectedMatchIds.size > 0 && onBulkDeleteMatches && (
            <span className="bulk-delete-bar">
              <span className="bulk-selection-count">{selectedMatchIds.size} seleccionado{selectedMatchIds.size !== 1 ? 's' : ''}</span>
              <button
                type="button"
                className="bulk-delete-btn"
                onClick={onBulkDeleteMatches}
                title="Eliminar matches seleccionados"
              >
                Eliminar seleccionados
              </button>
              {onClearMatchSelection && (
                <button
                  type="button"
                  className="bulk-clear-btn"
                  onClick={onClearMatchSelection}
                  title="Limpiar selección"
                >
                  ✕
                </button>
              )}
            </span>
          )}
        </div>
      )}

      {matchingProduct && type === 'matcheos' && (
        <div className={`matching-indicator matches-master-card ${!isMatching ? 'matching-complete' : ''}`}>
          {/* D3 (2026-06-07): ficha maestra rediseñada — thumbnail + título grande + códigos + marca +
              match count + UDM/CONTENIDO editables con botones. Muestra skeleton mientras `isMatching`. */}
          {(() => {
            const mFull = maestroFull || matchingProduct;
            const me = Array.isArray(mFull?.catalog_manually_edited)
              ? mFull.catalog_manually_edited
              : (typeof mFull?.catalog_manually_edited === 'string'
                  ? (() => { try { const p = JSON.parse(mFull.catalog_manually_edited); return Array.isArray(p) ? p : []; } catch { return []; } })()
                  : []);
            // Round 3 (2026-06-07) — Fix 5: la tabla `maestro` SÍ tiene columna
            // `link_imagen` con la URL canónica del thumb del producto maestro
            // (poblada vía importMaestro.js desde el Excel del cliente). Es la
            // fuente correcta. Si está vacía (productos antiguos sin foto),
            // fallback al thumb del primer match como heurística.
            const masterThumb = mFull?.link_imagen
              || (filteredProducts.find(p => p && p.imagen)?.imagen)
              || null;
            return (
              <div className="matches-master-inner">
                <div className="matches-master-thumb-col">
                  {masterThumb ? (
                    <img
                      src={masterThumb}
                      alt={mFull.nombre}
                      className="matches-master-thumb"
                      onClick={(e) => { e.stopPropagation(); handleImageClick(masterThumb, mFull.nombre); }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      title="Click para ver imagen en grande (derivado del primer match)"
                    />
                  ) : (
                    <div className="matches-master-thumb placeholder" aria-hidden="true" title="Sin imagen disponible">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                  )}
                  <div className={`matches-master-count ${maestroMatchCount === 0 ? 'is-zero' : ''}`} title={`${maestroMatchCount} matches`}>
                    <span className="num">{maestroMatchCount}</span>
                    <span className="lbl">{maestroMatchCount === 1 ? 'match' : 'matches'}</span>
                  </div>
                </div>
                <div className="matches-master-body">
                  <div className="matches-master-title">{mFull?.nombre || matchingProduct.nombre}</div>
                  <div className="matches-master-meta">
                    <span className="meta-chip">
                      <span className="meta-chip-label">Cod producto</span>
                      <span className="meta-chip-value">{mFull?.cod_producto || matchingProduct.cod_producto || '-'}</span>
                    </span>
                    <span className="meta-chip">
                      <span className="meta-chip-label">Cod TIA</span>
                      <span className="meta-chip-value">{mFull?.cod_producto_tia || matchingProduct.cod_producto_tia || '-'}</span>
                    </span>
                    {(mFull?.marca || mFull?.Marca) && (
                      <span className="meta-chip">
                        <span className="meta-chip-label">Marca</span>
                        <span className="meta-chip-value">{mFull.marca || mFull.Marca}</span>
                      </span>
                    )}
                  </div>

                  {/* Round 2 (2026-06-07) — Items 2 + 9 + 10.
                      Orden: CONTENIDO arriba, UDM abajo (más relevante visualmente
                      arriba). InlineEditCell con patrón SKU PT v2 (lápiz on-hover). */}
                  {onCatalogPatch && (mFull?.catalog_link_id || mFull?.id) ? (
                    <div className="matches-master-edits">
                      <InlineEditCell
                        label="Contenido"
                        value={mFull?.catalog_contenido}
                        type="number"
                        isOverride={me.includes('contenido')}
                        formatDisplay={formatContenido}
                        onSave={async (newVal) => {
                          await onCatalogPatch(mFull.catalog_link_id, { contenido: newVal });
                        }}
                        disabled={!mFull?.catalog_link_id}
                        disabledReason={!mFull?.catalog_link_id ? 'Sin matches todavía' : null}
                      />
                      <InlineEditCell
                        label="UDM"
                        value={mFull?.catalog_udm}
                        type="text"
                        suggestions={UDM_WHITELIST}
                        isOverride={me.includes('udm')}
                        onSave={async (newVal) => {
                          await onCatalogPatch(mFull.catalog_link_id, { udm: newVal });
                        }}
                        disabled={!mFull?.catalog_link_id}
                        disabledReason={!mFull?.catalog_link_id ? 'Sin matches todavía' : null}
                      />
                    </div>
                  ) : null}
                </div>

                {/* Round 3 (2026-06-07) — Fix 1: cancel-matching-btn (cruz roja) removido.
                    Sólo se renderizaba durante `isMatching`, pero el flow actual (selección
                    de maestro → fetch /api/maestro/:id/catalog) es <2s. El botón no aportaba
                    valor: deseleccionar es trivial (click en otro maestro). El user reportó
                    que no entendía para qué servía. Removed sin reemplazo. */}
              </div>
            );
          })()}

          {isMatching && (
            <div className="matches-skeleton">
              <div className="matches-skeleton-row"></div>
              <div className="matches-skeleton-row"></div>
              <div className="matches-skeleton-row"></div>
            </div>
          )}

          {showDebugPanel && (matchingDebug || matchingLogs.length > 0) && (
            <div className="debug-panel">
              {matchingDebug && (
                <>
                  <div className="debug-panel-header">
                    <strong>🤖 AI Matcher - Prompt Enviado a ChatGPT</strong>
                  </div>
                  <div className="debug-logs">
                    <div className="debug-log-entry">
                      <span className="log-message"><strong>Producto Maestro:</strong> {matchingDebug.maestroProduct}</span>
                    </div>
                    <div className="debug-log-entry">
                      <span className="log-message"><strong>Productos en Catálogo:</strong> {matchingDebug.catalogSize}</span>
                    </div>
                    <div className="debug-log-entry">
                      <span className="log-message"><strong>Tiempo de Respuesta:</strong> {matchingDebug.timeElapsed}ms</span>
                    </div>
                    <div className="debug-log-entry">
                      <span className="log-message"><strong>Tokens Usados:</strong> {matchingDebug.tokensUsed?.total_tokens || 'N/A'}</span>
                    </div>
                    <div className="debug-log-entry">
                      <details open>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px', color: '#4CAF50' }}>
                          📝 Ver Prompt Completo Enviado a ChatGPT
                        </summary>
                        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                          <strong>System Prompt:</strong>
                          <pre className="log-data" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#1e1e1e', padding: '10px', borderRadius: '4px' }}>{matchingDebug.systemPrompt}</pre>
                        </div>
                        <div style={{ marginTop: '10px' }}>
                          <strong>User Prompt:</strong>
                          <pre className="log-data" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#1e1e1e', padding: '10px', borderRadius: '4px' }}>{matchingDebug.prompt}</pre>
                        </div>
                      </details>
                    </div>
                    <div className="debug-log-entry">
                      <details>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>
                          ✅ Ver Respuesta de ChatGPT
                        </summary>
                        <pre className="log-data" style={{ whiteSpace: 'pre-wrap' }}>{matchingDebug.response}</pre>
                      </details>
                    </div>
                  </div>
                </>
              )}

              {matchingLogs.length > 0 && (
                <>
                  <div className="debug-panel-header" style={{ marginTop: matchingDebug ? '20px' : '0' }}>
                    <strong>📊 Log de Matching en Tiempo Real</strong>
                  </div>
                  <div className="debug-logs">
                    {matchingLogs.map((log, index) => (
                      <div key={index} className="debug-log-entry">
                        <span className="log-timestamp">[{log.timestamp}]</span>
                        <span className="log-message">{log.message}</span>
                        {log.data && (
                          <pre className="log-data">{log.data}</pre>
                        )}
                        {log.debugInfo && (
                          <div className="debug-prompt-section" style={{ marginTop: '10px', border: '1px solid #4CAF50', borderRadius: '4px', padding: '10px' }}>
                            <details open>
                              <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px', color: '#4CAF50' }}>
                                📝 Prompt Completo Enviado a ChatGPT
                              </summary>
                              <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                <strong>System Prompt:</strong>
                                <pre className="log-data" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#1e1e1e', padding: '10px', borderRadius: '4px' }}>{log.debugInfo.systemPrompt}</pre>
                              </div>
                              <div style={{ marginTop: '10px' }}>
                                <strong>User Prompt:</strong>
                                <pre className="log-data" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#1e1e1e', padding: '10px', borderRadius: '4px' }}>{log.debugInfo.userPrompt}</pre>
                              </div>
                            </details>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Round 2 (2026-06-07) — Item 4. Paginación arriba (sticky en flujo). */}
      {type === 'maestro' && !loading && (
        <PaginationCompact
          page={page}
          totalPages={totalPages}
          onChange={(p) => setPage(p)}
          position="top"
        />
      )}

      {(loading || (isMatching && filteredProducts.length === 0)) ? (
        <div className="loading">Cargando...</div>
      ) : type === 'matcheos' ? (
        // 2026-06-08 — Match cards (NO table). Estructura idéntica a master card
        // (mismo container, mismo ancho, mismo thumb-col + body-col). Solo bg distinto.
        <div className="match-cards-list">
          {filteredProducts.length === 0 ? (
            <div className="no-results no-match-results">
              {activeSearchTerm ? `No se encontraron productos para "${activeSearchTerm}"` : 'No se encontraron productos'}
            </div>
          ) : (
            getSortedProducts().map(({ product, score, reason }, displayIndex) => {
              const productIds = [
                product.id_catalogo ? String(product.id_catalogo) : null,
                product.title ? `title:${product.title}` : null
              ].filter(Boolean);
              const hasSiteWinner = product.site_winner === 1 || product.site_winner === '1';
              const isInCombinedMatches = productIds.some(id => combinedMatchIds.has(id));
              const isMatch = hasSiteWinner || isInCombinedMatches;
              const isSelected = selectedProduct?.id_catalogo === product.id_catalogo;
              const cardClasses = [
                'match-card',
                isSelected ? 'match-card--selected' : '',
                hasSiteWinner ? 'match-card--winner' : (isMatch ? 'match-card--match' : ''),
              ].filter(Boolean).join(' ');
              const sale = product.sale_price ? parseFloat(product.sale_price) : null;
              const orig = product.original_price ? parseFloat(product.original_price) : null;
              const dto = calculateDiscount(product.original_price, product.sale_price || product.discounted_price);
              const siteCodeLabel = (() => {
                if (!product.sitio) return 'Cod sitio';
                const s = String(product.sitio).toLowerCase();
                if (s.includes('frecuento')) return 'Cod Frecuento';
                if (s.includes('tipti')) return 'Cod Tipti';
                if (s.includes('tuti')) return 'Cod Tuti';
                return `Cod ${product.sitio}`;
              })();
              const checked = !!(selectedMatchIds && product.id_catalogo && selectedMatchIds.has(String(product.id_catalogo)));
              return (
                <div
                  key={displayIndex}
                  className={cardClasses}
                  onClick={() => { if (isMatch && onSelectProduct) onSelectProduct(product); }}
                  style={{ cursor: isMatch && onSelectProduct ? 'pointer' : 'default' }}
                >
                  <div className="match-card-inner">
                    <div className="match-card-thumb-col">
                      {product.imagen ? (
                        <img
                          src={product.imagen}
                          alt={product.title || ''}
                          className="match-card-thumb"
                          onClick={(e) => { e.stopPropagation(); handleImageClick(product.imagen, product.title); }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          title="Click para ver imagen en grande"
                        />
                      ) : (
                        <div className="match-card-thumb placeholder" aria-hidden="true" title="Sin imagen">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                        </div>
                      )}
                      {onToggleMatchSelection && (
                        <input
                          type="checkbox"
                          className="match-card-checkbox"
                          checked={checked}
                          onChange={() => onToggleMatchSelection(product.id_catalogo)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Seleccionar match"
                          title="Seleccionar para acción masiva"
                        />
                      )}
                    </div>
                    <div className="match-card-body">
                      {/* 1. SITIO */}
                      {product.sitio && (
                        <div className="match-site-row">
                          <span className="match-site-badge" data-site={product.sitio}>{product.sitio}</span>
                          {score !== null && score > 0 && (
                            reason ? (
                              <Tippy content={<div className="ai-tooltip-content"><div className="ai-tooltip-text">{reason}</div></div>} placement="top" arrow={true}>
                                <span className={`similarity-badge similarity-${Math.floor(score / 10) * 10}`}>{score}%</span>
                              </Tippy>
                            ) : <span className={`similarity-badge similarity-${Math.floor(score / 10) * 10}`}>{score}%</span>
                          )}
                        </div>
                      )}
                      {/* 2. Título */}
                      <div className="match-card-title">
                        <Tippy content={product.title || '-'} placement="top" arrow={true} disabled={!product.title || product.title.length < 50}>
                          <span>{product.title || '-'}</span>
                        </Tippy>
                        {product.url && (
                          <a href={product.url} target="_blank" rel="noopener noreferrer" title={product.url} className="product-link" onClick={(e) => e.stopPropagation()}>↗</a>
                        )}
                      </div>
                      {/* 3. Codes — cod producto (del maestro vinculado) + cod del sitio */}
                      <div className="match-codes-row">
                        {(product.cod_producto || matchingProduct?.cod_producto || maestroFull?.cod_producto) && (
                          <span className="meta-chip">
                            <span className="meta-chip-label">Cod producto</span>
                            <span className="meta-chip-value">{product.cod_producto || matchingProduct?.cod_producto || maestroFull?.cod_producto}</span>
                          </span>
                        )}
                        {product.id_catalogo && (
                          <span className="meta-chip">
                            <span className="meta-chip-label">{siteCodeLabel}</span>
                            <span className="meta-chip-value">{product.id_catalogo}</span>
                          </span>
                        )}
                      </div>
                      {/* 4. Cat + Subcat */}
                      {((product.Cat || product.categoria) || (product.Subcat || product.subcategoria)) && (
                        <div className="match-cats-row">
                          {(product.Cat || product.categoria) && (
                            <span className="match-cat-chip">{product.Cat || product.categoria}</span>
                          )}
                          {(product.Subcat || product.subcategoria) && (
                            <span className="match-cat-chip subcat">{product.Subcat || product.subcategoria}</span>
                          )}
                        </div>
                      )}
                      {/* 5. Precios — prefix con fecha + toma */}
                      {(sale !== null || orig !== null) && (() => {
                        // fecha + toma del precio mostrado. Tomar de price_fecha + intake del match,
                        // o si el catalog endpoint ya devuelve current_intake, preferirlo.
                        const priceFechaRaw = product.current_intake_fecha || product.intake_fecha || product.fecha || product.price_fecha;
                        const intakeStr = product.current_intake || product.intake;
                        let dStr = '', tStr = '';
                        if (priceFechaRaw) {
                          try {
                            const d = new Date(priceFechaRaw);
                            if (!Number.isNaN(d.getTime())) {
                              dStr = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getFullYear()).slice(2)}`;
                            }
                          } catch {}
                        }
                        if (intakeStr) {
                          const m2 = String(intakeStr).match(/toma[_-]?(\d+)/i);
                          tStr = m2 ? `Toma ${m2[1]}` : intakeStr;
                        }
                        const label = (dStr || tStr) ? `Precio (${[dStr, tStr].filter(Boolean).join(' - ')}):` : 'Precio:';
                        return (
                          <div className="match-price-row">
                            <span className="match-price-label">{label}</span>
                            {sale !== null && (
                              <span className="match-price-sale">${sale.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            )}
                            {sale === null && orig !== null && (
                              <span className="match-price-sale">${orig.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            )}
                            {sale !== null && orig !== null && orig > sale && (
                              <span className="match-price-original">${orig.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            )}
                            {dto && (<span className="match-price-discount">-{dto}%</span>)}
                          </div>
                        );
                      })()}
                      {/* 6. Botones (Histórico izq + Eliminar der) + panel histórico full-width debajo */}
                      <MatchCardActions
                        idCatalogo={product.id_catalogo}
                        sitio={product.sitio}
                        onDelete={onDeleteMatch}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <>
          <div className={`table-wrapper ${type === 'maestro' ? 'product-table-scroll' : ''}`}>
            <table className={`product-table ${type === 'maestro' ? 'maestro-table' : ''}`}>
              <thead>
                <tr>
                  {type === 'maestro' ? (
                    <>
                      <th>Nombre</th>
                    </>
                  ) : (
                    <>
                      <th>Nombre</th>
                      <th>Sitio</th>
                      <th>Precio</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        type === 'maestro'
                          ? 1
                          : type === 'matcheos' && onToggleMatchSelection
                            ? 4
                            : 3
                      }
                      className="no-results"
                    >
                      {/* UI Polish R1 (2026-06-07): empty state contextual para Toma.
                          Distingue "sin maestro seleccionado" vs "sin resultados de búsqueda". */}
                      {type === 'toma' && noMaestroSelected && !activeSearchTerm ? (
                        <div className="empty-state empty-state-no-master">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M16.5 9.4 7.55 4.24"></path>
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                            <line x1="12" y1="22.08" x2="12" y2="12"></line>
                          </svg>
                          <div className="empty-state-text">
                            Seleccioná un producto del maestro para ver items relacionados de esta toma
                          </div>
                        </div>
                      ) : type === 'toma' && activeSearchTerm ? (
                        `No se encontraron productos para "${activeSearchTerm}"`
                      ) : (
                        'No se encontraron productos'
                      )}
                    </td>
                  </tr>
                ) : (
                  getSortedProducts().map(({ product, score, reason }, displayIndex) => {
                    // Para productos de catálogo (toma/matcheos), marcar como match si:
                    // 1. El producto tiene site_winner = 1 en matching_process (prioritario)
                    // 2. O si está en los IDs combinados de matches (winnerCatalogIds o aiMatchedCatalogIds)
                    const productIds = [
                      product.id_catalogo ? String(product.id_catalogo) : null,
                      product.title ? `title:${product.title}` : null
                    ].filter(Boolean);

                    // Verificar si es match: prioritariamente por site_winner, luego por combinedMatchIds
                    const hasSiteWinner = product.site_winner === 1 || product.site_winner === '1';
                    const isInCombinedMatches = productIds.some(id => combinedMatchIds.has(id));
                    const isMatch = (type === 'toma' || type === 'matcheos') && (hasSiteWinner || isInCombinedMatches);

                    // Determinar si este producto está seleccionado
                    const isSelected = type === 'maestro'
                      ? selectedProduct?.cod_producto === product.cod_producto
                      : selectedProduct?.id_catalogo === product.id_catalogo;

                    // Determinar la clase CSS
                    let rowClassName = '';
                    if (type === 'maestro') {
                      if (isSelected && product.has_matches === 1) {
                        // Producto con matches seleccionado -> verde oscuro
                        rowClassName = 'product-selected-with-matches';
                      } else if (isSelected) {
                        // Producto sin matches seleccionado -> otro estilo
                        rowClassName = 'product-selected-no-matches';
                      } else if (product.has_matches === 1) {
                        rowClassName = 'maestro-has-matches';
                      }
                    } else {
                      // Para catálogo/matcheos: primero verificar si está seleccionado, luego distinguir entre site_winner y match normal
                      if (isSelected) {
                        rowClassName = 'product-selected-catalog';
                      } else if (hasSiteWinner) {
                        rowClassName = 'product-site-winner';
                      } else if (isMatch) {
                        rowClassName = 'product-match';
                      }
                    }

                    // Handler para iniciar drag desde catálogo
                    const handleDragStart = (e) => {
                      if (type === 'toma') {
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          id_catalogo: product.id_catalogo,
                          title: product.title,
                          sitio: product.sitio,
                          imagen: product.imagen
                        }));
                        e.dataTransfer.effectAllowed = 'copy';
                      }
                    };

                    return (
                    <React.Fragment key={displayIndex}>
                      <tr
                        className={rowClassName}
                        draggable={type === 'toma'}
                        onDragStart={handleDragStart}
                        onClick={() => {
                          if (type === 'maestro' && onSelectProduct) {
                            onSelectProduct(product);
                          } else if ((type === 'toma' || type === 'matcheos') && isMatch && onSelectProduct) {
                            // Permitir clic en todos los productos que sean matches
                            onSelectProduct(product);
                          }
                        }}
                        style={{
                          cursor: (type === 'maestro' && onSelectProduct) || ((type === 'toma' || type === 'matcheos') && isMatch && onSelectProduct)
                            ? 'pointer'
                            : type === 'toma' ? 'grab'
                            : 'default'
                        }}
                      >
                        {type === 'maestro' ? (
                          <>
                            <td className="product-name maestro-cell">
                              {(() => {
                                const idStr = product.id != null ? String(product.id) : null;
                                const mc = (matchCounts && idStr && matchCounts[idStr]) || null;
                                const cnt = mc ? mc.count : 0;
                                const sitios = mc ? mc.sitios : [];
                                // Round 2 (2026-06-07) — Item 5.
                                // Pastilla "Matches: N" alineada derecha en la fila de chips
                                // COD/TIA. Sólo se renderiza si N>=1 (si N=0 el row sólo tiene
                                // COD + TIA). Tooltip nativo con sitios + Tippy enriquecido.
                                const titleAttr = (cnt > 0 && sitios.length > 0) ? sitios.join(', ') : undefined;
                                const pastilla = cnt >= 1 ? (
                                  <span
                                    className="maestro-match-count"
                                    aria-label={`${cnt} matches${titleAttr ? ` en ${titleAttr}` : ''}`}
                                    title={titleAttr}
                                  >
                                    {cnt}
                                  </span>
                                ) : null;
                                const pastillaWrapped = pastilla && sitios.length > 0 ? (
                                  <Tippy
                                    content={<div className="match-count-tooltip">{sitios.join(', ')}</div>}
                                    placement="left"
                                    arrow={true}
                                    delay={[200, 0]}
                                  >
                                    {pastilla}
                                  </Tippy>
                                ) : pastilla;
                                return (
                                  <div className="maestro-row-main">
                                    <div className="maestro-codes-row">
                                      <span className="code-chip">
                                        <span className="code-chip-label">Cod</span>
                                        <span className="code-chip-value">{product.cod_producto || '-'}</span>
                                      </span>
                                      <span className="code-chip">
                                        <span className="code-chip-label">TIA</span>
                                        <span className="code-chip-value">{product.cod_producto_tia || '-'}</span>
                                      </span>
                                      {pastillaWrapped}
                                    </div>
                                    <Tippy
                                      content={product.nombre || '-'}
                                      placement="top"
                                      arrow={true}
                                      delay={[300, 0]}
                                      disabled={!product.nombre || product.nombre.length < 40}
                                    >
                                      <span className="maestro-product-title">{product.nombre || '-'}</span>
                                    </Tippy>
                                  </div>
                                );
                              })()}
                            </td>
                          </>
                        ) : (
                          <>
                          {type === 'matcheos' && onToggleMatchSelection && (
                            <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={!!(selectedMatchIds && product.id_catalogo && selectedMatchIds.has(String(product.id_catalogo)))}
                                onChange={() => onToggleMatchSelection(product.id_catalogo)}
                                aria-label="Seleccionar match"
                              />
                            </td>
                          )}
                          <td className="product-name">
                            <div className="product-name-wrapper">
                              {/* D3 (2026-06-07) — Toma: botón "+ Agregar al Match" a la izquierda */}
                              {type === 'toma' && onAddMatchClick && (
                                <button
                                  type="button"
                                  className="add-match-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAddMatchClick({
                                      id_catalogo: product.id_catalogo,
                                      title: product.title,
                                      sitio: product.sitio,
                                      imagen: product.imagen,
                                    });
                                  }}
                                  title="Agregar al Match"
                                  aria-label="Agregar al Match"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                  </svg>
                                  <span>Agregar</span>
                                </button>
                              )}
                              {type === 'toma' && (
                                <span className="drag-handle-icon-row" title="Arrastrar a Matches">
                                  <span className="drag-grip">
                                    <span></span><span></span>
                                    <span></span><span></span>
                                    <span></span><span></span>
                                  </span>
                                </span>
                              )}
                              {/* D3 (2026-06-07) — Matcheos: caret expand eliminado, todo siempre visible.
                                  Toma: caret se mantiene para detalle expandido con UDM/CONTENIDO editables. */}
                              {type === 'toma' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowExpansion(displayIndex);
                                  }}
                                  className="expand-caret-btn"
                                  title={expandedRows.has(displayIndex) ? "Ocultar detalles" : "Mostrar detalles"}
                                >
                                  {expandedRows.has(displayIndex) ? '▼' : '▶'}
                                </button>
                              )}
                              {product.imagen ? (
                                <img
                                  src={product.imagen}
                                  alt={product.title}
                                  className="product-thumbnail"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleImageClick(product.imagen, product.title);
                                  }}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  title="Click para ver imagen en grande"
                                />
                              ) : null}
                              <div className="product-name-content">
                                <Tippy content={product.title || '-'} placement="top" arrow={true} delay={[300, 0]} disabled={!product.title || product.title.length < 50}>
                                  <span className="product-title">{product.title || '-'}</span>
                                </Tippy>
                                {product.url && (
                                  <a href={product.url} target="_blank" rel="noopener noreferrer" title={product.url} className="product-link" onClick={(e) => e.stopPropagation()}>
                                    ↗
                                  </a>
                                )}
                                {/* Round 3 (2026-06-07) — Fix 3.
                                    Match item rediseñado con la misma rítmica que la master card:
                                      1. Pastilla SITIO arriba
                                      2. Título (renderizado arriba por product-title, ver markup)
                                      3. Pastillas COD producto + COD del sitio (label dinámico)
                                      4. Pastillas Categoría + Subcategoría
                                      5. Precio | Precio original tachado | Descuento %
                                      6. Botones: Histórico (izq) + Eliminar (der)
                                    UDM/CONTENIDO viven en la master card (no se duplican acá). */}
                                {type === 'matcheos' && (() => {
                                  const sale = product.sale_price ? parseFloat(product.sale_price) : null;
                                  const orig = product.original_price ? parseFloat(product.original_price) : null;
                                  const dto = calculateDiscount(product.original_price, product.sale_price || product.discounted_price);
                                  // Label dinámico para el COD del sitio según el sitio del match.
                                  // "Cod Frecuento", "Cod Tipti", "Cod Tuti" → fallback "Cod sitio" si desconocido.
                                  const siteCodeLabel = (() => {
                                    if (!product.sitio) return 'Cod sitio';
                                    const s = String(product.sitio).toLowerCase();
                                    if (s.includes('frecuento')) return 'Cod Frecuento';
                                    if (s.includes('tipti')) return 'Cod Tipti';
                                    if (s.includes('tuti')) return 'Cod Tuti';
                                    return `Cod ${product.sitio}`;
                                  })();
                                  return (
                                    <>
                                      {/* 1. SITIO arriba (pastilla destacada) */}
                                      {product.sitio && (
                                        <div className="match-site-row">
                                          <span className="match-site-badge" data-site={product.sitio}>{product.sitio}</span>
                                        </div>
                                      )}
                                      {/* 3. COD producto (maestro vinculado) + COD del sitio (id_catalogo).
                                          Renderizamos sólo los que tienen valor para no ensuciar la card. */}
                                      <div className="match-codes-row">
                                        {product.cod_producto && (
                                          <span className="meta-chip">
                                            <span className="meta-chip-label">Cod producto</span>
                                            <span className="meta-chip-value">{product.cod_producto}</span>
                                          </span>
                                        )}
                                        {product.id_catalogo && (
                                          <span className="meta-chip">
                                            <span className="meta-chip-label">{siteCodeLabel}</span>
                                            <span className="meta-chip-value">{product.id_catalogo}</span>
                                          </span>
                                        )}
                                      </div>
                                      {/* 4. Categoría + Subcategoría */}
                                      {((product.Cat || product.categoria) || (product.Subcat || product.subcategoria)) && (
                                        <div className="match-cats-row">
                                          {(product.Cat || product.categoria) && (
                                            <span className="match-cat-chip">{product.Cat || product.categoria}</span>
                                          )}
                                          {(product.Subcat || product.subcategoria) && (
                                            <span className="match-cat-chip subcat">{product.Subcat || product.subcategoria}</span>
                                          )}
                                        </div>
                                      )}
                                      {/* 5. Precio | Original tachado | Descuento */}
                                      {(sale !== null || orig !== null) && (
                                        <div className="match-price-row">
                                          {sale !== null && (
                                            <span className="match-price-sale">${sale.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                          )}
                                          {sale === null && orig !== null && (
                                            <span className="match-price-sale">${orig.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                          )}
                                          {sale !== null && orig !== null && orig > sale && (
                                            <span className="match-price-original">${orig.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                          )}
                                          {dto && (
                                            <span className="match-price-discount">-{dto}%</span>
                                          )}
                                        </div>
                                      )}
                                      {/* 6. Botones acción: Histórico izq + Eliminar der */}
                                      <div className="match-actions-row">
                                        {product.id_catalogo && (
                                          <PriceHistoryToggle idCatalogo={product.id_catalogo} sitio={product.sitio} />
                                        )}
                                        {onDeleteMatch && (
                                          <button
                                            className="delete-match-btn inline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onDeleteMatch(product.id_catalogo);
                                            }}
                                            title="Eliminar este match"
                                            aria-label="Eliminar match"
                                          >
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                                              <polyline points="3 6 5 6 21 6"></polyline>
                                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                            </svg>
                                            Eliminar
                                          </button>
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                                {score !== null && score > 0 && (
                                  reason ? (
                                    <Tippy
                                      content={
                                        <div className="ai-tooltip-content">
                                          <div className="ai-tooltip-header">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <rect x="3" y="11" width="18" height="10" rx="2"></rect>
                                              <circle cx="12" cy="5" r="2"></circle>
                                              <path d="M12 7v4"></path>
                                              <line x1="8" y1="16" x2="8" y2="16"></line>
                                              <line x1="16" y1="16" x2="16" y2="16"></line>
                                            </svg>
                                            <span>AI Analysis</span>
                                          </div>
                                          <div className="ai-tooltip-text">{reason}</div>
                                        </div>
                                      }
                                      placement="top"
                                      animation="scale"
                                      theme="ai-custom"
                                      arrow={true}
                                    >
                                      <span className={`similarity-badge similarity-${Math.floor(score / 10) * 10}`}>
                                        {score}%
                                      </span>
                                    </Tippy>
                                  ) : (
                                    <span className={`similarity-badge similarity-${Math.floor(score / 10) * 10}`}>
                                      {score}%
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{product.sitio || '-'}</td>
                          <td className="price-combined">
                            {(() => {
                              const sale = product.sale_price ? parseFloat(product.sale_price) : null;
                              const original = product.original_price ? parseFloat(product.original_price) : null;
                              const discount = calculateDiscount(product.original_price, product.sale_price || product.discounted_price);
                              if (sale === null && original === null) return '-';
                              if (sale !== null) {
                                return (
                                  <span className="price-wrap">
                                    <span className="price-sale">${sale.toFixed(2)}</span>
                                    {original !== null && original > sale && (
                                      <span className="price-original-strike">${original.toFixed(2)}</span>
                                    )}
                                    {discount && (
                                      <span className="discount-badge">-{discount}%</span>
                                    )}
                                  </span>
                                );
                              }
                              return <span className="price-sale">${original.toFixed(2)}</span>;
                            })()}
                          </td>
                          </>
                        )}
                      </tr>
                    {/* D3 (2026-06-07): Maestro ya no es expandible (códigos inline).
                        Matcheos ya no es expandible (todo siempre visible).
                        Toma SÍ es expandible con UDM/CONTENIDO editables + categoría + marca + link. */}
                    {type === 'toma' && expandedRows.has(displayIndex) && (
                      <tr className="expanded-row">
                        <td colSpan={3} className="expanded-content">
                          <div className="toma-detail-grid">
                            <div className="toma-detail-meta">
                              <span className="code-chip">
                                <span className="code-chip-label">ID catálogo</span>
                                <span className="code-chip-value">{product.id_catalogo || '-'}</span>
                              </span>
                              <span className="code-chip">
                                <span className="code-chip-label">Categoría</span>
                                <span className="code-chip-value">{parseTag(product.tags, 'CATEGORY') || product.Cat || '-'}</span>
                              </span>
                              <span className="code-chip">
                                <span className="code-chip-label">Subcategoría</span>
                                <span className="code-chip-value">{parseTag(product.tags, 'SUBCATEGORY') || '-'}</span>
                              </span>
                              {(product.Marca || product.marca) && (
                                <span className="code-chip">
                                  <span className="code-chip-label">Marca</span>
                                  <span className="code-chip-value">{product.Marca || product.marca}</span>
                                </span>
                              )}
                              {product.url && (
                                <a href={product.url} target="_blank" rel="noopener noreferrer" className="toma-detail-link">
                                  Ver en sitio ↗
                                </a>
                              )}
                            </div>
                            {onCatalogPatch && product.id_catalogo && (() => {
                              const cme = Array.isArray(product.catalog_manually_edited)
                                ? product.catalog_manually_edited
                                : (typeof product.catalog_manually_edited === 'string'
                                    ? (() => { try { const p = JSON.parse(product.catalog_manually_edited); return Array.isArray(p) ? p : []; } catch { return []; } })()
                                    : []);
                              return (
                                <div className="toma-detail-edits">
                                  {/* Items 2 + 9: CONTENIDO arriba, UDM abajo */}
                                  <InlineEditCell
                                    label="Contenido"
                                    value={product.contenido || product.catalog_contenido}
                                    type="number"
                                    isOverride={cme.includes('contenido')}
                                    formatDisplay={formatContenido}
                                    onSave={async (newVal) => {
                                      await onCatalogPatch(product.id_catalogo, { contenido: newVal });
                                    }}
                                  />
                                  <InlineEditCell
                                    label="UDM"
                                    value={product.udm || product.catalog_udm}
                                    type="text"
                                    suggestions={UDM_WHITELIST}
                                    isOverride={cme.includes('udm')}
                                    onSave={async (newVal) => {
                                      await onCatalogPatch(product.id_catalogo, { udm: newVal });
                                    }}
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Round 2 (2026-06-07) — Item 4. Paginación abajo (mismo componente).
              Para maestro usa PaginationCompact (compacta, 2-líneas, columna estrecha).
              Para toma/matcheos mantiene la paginación clásica anterior. */}
          {type === 'maestro' ? (
            <PaginationCompact
              page={page}
              totalPages={totalPages}
              onChange={(p) => setPage(p)}
              position="bottom"
            />
          ) : (
            totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className="pagination-btn"
                >
                  Anterior
                </button>
                <span className="page-info">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                  className="pagination-btn"
                >
                  Siguiente
                </button>
              </div>
            )
          )}
        </>
      )}

      {/* Modal de imagen */}
      {imageModal.open && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>
              ×
            </button>
            <img src={imageModal.url} alt={imageModal.title} className="image-modal-img" />
            {imageModal.title && <p className="image-modal-title">{imageModal.title}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductTable;
